import { useEffect, useState } from "react";
import type { AvailableTermsSetMap, SelectedTerms } from "@/features/productVariations/types";
import { ProductType, type Product, type ProductAttribute } from "@/types/products";
import type { ProductVariation } from "@/types/productVariations";

interface UseAttributeSelectionReturn {
  selectedTerms: SelectedTerms;
  handleAttributeSelect: (attributeName: string, termName: string) => void;
}

// helper
const getDefaultTerm = (
  availableTermsSetMap: AvailableTermsSetMap,
  inStockVariations: ProductVariation[],
  attr: ProductAttribute,
): string | null => {
  const attributeTermsAry = Array.from(availableTermsSetMap?.[attr.name] || []);

  return (
    attributeTermsAry.find((term) =>
      inStockVariations.some((inStockV) =>
        inStockV.attributes.some(
          (inStockAttr) => inStockAttr.name === attr.name && inStockAttr.option === term,
        ),
      ),
    ) || null
  );
};

/**
 * 管理 attribute 屬性選擇
 * 職責：管理用戶選擇的屬性，並在變體資料載入後設定預設選項
 */
const useAttributeSelection = (
  product: Product | null,
  stockedVariations: ProductVariation[] | null,
  inStockVariations: ProductVariation[],
  initialAvailableTermsSetMap: AvailableTermsSetMap,
): UseAttributeSelectionReturn => {
  const [selectedTerms, setSelectedTerms] = useState<SelectedTerms>({});

  const handleAttributeSelect = (attributeName: string, termName: string) => {
    setSelectedTerms((prevSelected) => ({
      ...prevSelected,
      [attributeName]: prevSelected[attributeName] !== termName ? termName : null,
    }));
  };

  // 當 product 切換時，立即重置狀態
  useEffect(() => {
    setSelectedTerms({});
  }, [product?.id]);

  // 當變體資料載入完成後，設定預設選項
  useEffect(() => {
    if (!product || product.type !== ProductType.Variable || !stockedVariations) return;

    // 預設選取第一個屬性中有庫存的第一個 term，第二個屬性預設 null 不預先選取
    const defaultTerms = product.attributes.reduce((acc, attr, index) => {
      const defaultTerm =
        index === 0 ? getDefaultTerm(initialAvailableTermsSetMap, inStockVariations, attr) : null;
      return { ...acc, [attr.name]: defaultTerm };
    }, {} as SelectedTerms);

    setSelectedTerms(defaultTerms);
  }, [product?.id, stockedVariations, initialAvailableTermsSetMap, inStockVariations]);

  return { selectedTerms, handleAttributeSelect };
};

export default useAttributeSelection;
